import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Menu, {MenuProps} from 'ui-library/menus/Menu';
import {MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';

export interface MenuOptionWithFloatingSubmenuProps {
  /** The upstream container of the menu option. This is needed to calculate the maxHeight of the submenu. */
  menuOptionContainer: HTMLElement | null;
  /** Index of the menu option in the parent menu. */
  menuOptionIndex: number;
  /** Data needed for rendering the menu option. */
  optionData: OptionData;
  /** This function renders the menu option. */
  renderOptionContentFn: (optionData: OptionData, shouldFade?: boolean | undefined) => JSX.Element;
  /** Props for the submenu. */
  submenuProps: Partial<MenuProps>;
  /**
   * Whether the submenu should be set to open for testing.
   * @default false
   */
  openSubmenuForTesting?: boolean;
  /** Element to be rendered in the submenu. */
  children: Array<JSX.Element>;
}

export enum DropPosition {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  OVERLAP = 'OVERLAP', // no enough space on either side
}

export const getDropPosition = (menuOptionContainer: HTMLElement, windowWidth: number): DropPosition => {
  const {left, right, width} = menuOptionContainer.getBoundingClientRect();
  if (windowWidth - right >= width) {
    return DropPosition.RIGHT;
  } else if (left >= width) {
    return DropPosition.LEFT;
  } else {
    return DropPosition.OVERLAP;
  }
};

const MenuWidths = [
  MenuWidth.FULL,
  MenuWidth.XXLARGE,
  MenuWidth.XLARGE,
  MenuWidth.LARGE,
  MenuWidth.MEDIUM,
  MenuWidth.SMALL,
  MenuWidth.MINI,
];

export const getWidth = (
  dropPosition: DropPosition,
  parentMenuWidth: MenuWidth | undefined
): MenuWidth | undefined => {
  if (parentMenuWidth == null || dropPosition !== DropPosition.OVERLAP) {
    // The width cannot be FULL, because FULL sets the width to 100%.
    // The floating submenu is rendered in a separate portal and will
    // disappear with a 100% width.
    return parentMenuWidth === Menu.WIDTHS.FULL ? Menu.WIDTHS.XXLARGE : parentMenuWidth;
  }

  // When there is not enough space on either side, use the next smaller width.
  // The exceptions are MenuWidth.FIT_CONTENT and MenuWidth.MINI.
  const menuWidthIndex = MenuWidths.indexOf(parentMenuWidth);
  if (menuWidthIndex === -1 || menuWidthIndex === MenuWidths.length - 1) {
    return parentMenuWidth;
  } else {
    return MenuWidths[menuWidthIndex + 1];
  }
};

/**
 * This component renders a menu option that can open a floating submenu.
 *
 * In production, the submenu opens on hover. In enzyme tests, this component accepts
 * an "openedForTesting" flag and renders a ControlledFloat with isOpened set to true.
 */
export default function MenuOptionWithFloatingSubmenu({
  menuOptionContainer,
  menuOptionIndex,
  optionData,
  renderOptionContentFn,
  submenuProps,
  openSubmenuForTesting = false,
  children,
}: MenuOptionWithFloatingSubmenuProps): JSX.Element {
  const [dropLeft, setDropLeft] = React.useState<boolean | undefined>(submenuProps.dropLeft);
  const [width, setWidth] = React.useState<MenuWidth | undefined>(submenuProps.width);

  React.useEffect(() => {
    if (submenuProps.dropLeft != null || menuOptionContainer == null || window == null) {
      return;
    }

    // Should we fully depend on the useFloatingUI hook from the floating-ui, the position
    // and width of the floating submenu could be calculated automatically by the flip and
    // size middleware. However, the width and position are partially controlled by CSS in
    // menu.less. So we need to manually calculate the dropLeft and width props here.
    const calculatePositionAndWidth = (): void => {
      const newDropPosition = getDropPosition(menuOptionContainer, window.innerWidth);
      const newWidth = getWidth(newDropPosition, submenuProps.width);
      setDropLeft(newDropPosition === DropPosition.LEFT);
      setWidth(newWidth);
    };

    calculatePositionAndWidth();

    window.addEventListener('resize', calculatePositionAndWidth);
    return () => {
      window.removeEventListener('resize', calculatePositionAndWidth);
    };
  }, [menuOptionContainer, submenuProps.dropLeft, submenuProps.width]);

  return (
    <Float
      interaction={openSubmenuForTesting ? FloatingInteraction.CONTROLLED : FloatingInteraction.HOVER}
      isOpen={openSubmenuForTesting}
      placement={dropLeft ? FloatingPlacement.LEFT_START : FloatingPlacement.RIGHT_START}
      floatingElementOptions={{
        floatingBodyClassName: css(styles.floatingBody),
        dataTestAttrs: optionData.dataTestAttrs,
      }}
      role="dialog"
      renderContent={() => (
        <Menu
          parentMenuOptionContainer={menuOptionContainer}
          key={menuOptionIndex}
          scrollable={true}
          {...submenuProps}
          width={width}
          dropLeft={dropLeft}
        >
          {children}
        </Menu>
      )}
    >
      <div
        className={css(styles.menuOption)}
        {...getTestProps({
          component: 'MenuOptionWithFloatingSubmenu',
          element: 'optionContent',
        })}
      >
        {renderOptionContentFn(optionData)}
      </div>
    </Float>
  );
}

const styles = StyleSheet.create({
  menuOption: {
    width: '100%',
  },
  floatingBody: {
    boxShadow: 'none',
  },
});
